import React, { FC, useMemo, useState } from 'react';

import { Box } from '@chakra-ui/react';

import { StackedCell } from 'components/DataDisplay';
import { ButtonGroup } from 'components/DataEntry';
import { ButtonGroupItem } from 'components/DataEntry/Button/ButtonGroup';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

export type AccessScope = 'all' | 'internal' | 'external';

/** Anything the access summaries return that names the account it lives in. */
interface ScopedRow {
  external?: boolean;
}

/**
 * The All / This Account / Other Accounts selector, and the rows it leaves
 * showing.
 *
 * One list with the account as a column replaced the pair of internal and
 * external sections these tables used to have: rows from both accounts can
 * then be compared and sorted in one place instead of across two headings.
 */
export const useAccessScope = <T extends ScopedRow>(rows: T[]) => {
  const [selected, setSelected] = useState<AccessScope>('all');

  const hasExternal = useMemo(() => rows.some(r => r.external), [rows]);

  /**
   * The rows a caller passes can change under a selection -- another resource,
   * a refresh, or a filter of its own. Callers hide the selector when there is
   * nothing in another account, so a stored `external` would then filter every
   * row away with no control left to undo it. The choice is remembered rather
   * than reset, so it applies again if those rows come back.
   */
  const scope: AccessScope = hasExternal ? selected : 'all';

  const scopedRows = useMemo(() => {
    if (scope === 'internal') return rows.filter(r => !r.external);
    if (scope === 'external') return rows.filter(r => r.external);
    return rows;
  }, [rows, scope]);

  const scopeItems: ButtonGroupItem[] = useMemo(() => {
    const internalCount = rows.filter(r => !r.external).length;
    return [
      { label: `All ${rows.length}`, value: 'all' },
      { label: `This Account ${internalCount}`, value: 'internal' },
      {
        label: `Other Accounts ${rows.length - internalCount}`,
        value: 'external',
      },
    ];
  }, [rows]);

  return { scope, setScope: setSelected, scopedRows, scopeItems, hasExternal };
};

interface AccessScopeButtonsProps {
  items: ButtonGroupItem[];
  scope: AccessScope;
  onChange(scope: AccessScope): void;
}

/**
 * The selector as an accordion's right header. The click is stopped here
 * because the header it sits in is the accordion's own expand button.
 */
export const AccessScopeButtons: FC<AccessScopeButtonsProps> = props => {
  const { items, scope, onChange } = props;
  return (
    <Box onClick={e => e.stopPropagation()}>
      <ButtonGroup
        items={items}
        value={items.find(i => i.value === scope)}
        onClick={item => onChange(item.value)}
      />
    </Box>
  );
};

/**
 * The account column that goes with the selector: the cloud account id, with
 * whose account it is below it. Every row carries one now that the account is
 * no longer said by which section a row is in.
 */
export const AccountCell: FC<{
  accountId?: string | null;
  external?: boolean;
}> = ({ accountId, external }) => {
  const { toCloudAccountId, toCloudAccountName } = useCloudAccountId();
  return (
    <StackedCell
      upper={toCloudAccountId(accountId)}
      lower={external ? toCloudAccountName(accountId) : 'This Account'}
    />
  );
};
